/**
 * Client ของ Supplier Master — พูดกับ `/v1/suppliers` ตาม payload contract ที่
 * freeze ไว้ใน `docs/API_CONTRACT.md` (D-083)
 *
 * หลักการเดียวกับ `lib/rbacAdmin/client.ts` และ `lib/bom/client.ts`:
 * - ฟังก์ชันในไฟล์นี้ **ไม่ throw** ความล้มเหลวทุกแบบกลายเป็นสถานะที่เรียกดูได้
 *   ผู้เรียกจึงเผลอ `catch` แล้วเดาเป็น "สำเร็จ" ไม่ได้
 * - payload ที่ผิดรูปคือ `invalid-response` **ไม่ใช่ "ยังไม่มีผู้ขาย"** — รายการว่าง
 *   กับโหลดไม่สำเร็จต้องแยกกันให้ขาด ไม่งั้นผู้ใช้จะอ่านว่า "ยังไม่มีใครลงทะเบียน
 *   ผู้ขายไว้" แล้วสร้างรหัสที่มีเจ้าของอยู่แล้วซ้ำเข้าไป
 * - **ไม่มี fallback ที่แต่งข้อมูลขึ้นมาเอง** และ **ไม่เขียนอะไรลง storage เลย**
 *   (D-049 อนุญาตเฉพาะ session token)
 * - ทุกคำขอเดินทางผ่าน `apiFetch` ตัวเดียวกับ client อื่นทุกตัว header
 *   `Authorization: Bearer` ถูกเติมที่นั่นจุดเดียว **ไม่ใช่ที่นี่**
 *
 * เส้นทางที่ contract freeze ไว้มีสี่เส้น และไฟล์นี้ implement สาม —
 * `GET /v1/suppliers/{supplier_id}` ยังไม่มีฟังก์ชันของมัน เพราะยังไม่มีหน้าจอไหน
 * อ่านผู้ขายรายเดียวจากเซิร์ฟเวอร์: กล่องแก้ไขเปิดจาก **แถวที่ list ส่งมาแล้ว** และ
 * ฟังก์ชันที่เขียนทิ้งไว้ก่อนวันที่มีคนเรียกใช้คือปุ่มที่ไม่มีใครทดสอบ (เหตุผล
 * เดียวกับที่ `lib/rbacAdmin/client.ts` เว้น `employees` ไว้จนถึงวันที่มีหน้าจอจริง)
 *
 * ⚠️ ไม่ใช่ด่านความปลอดภัย เซิร์ฟเวอร์เป็นผู้ตัดสินทุก request เสมอ
 * (`references/authorization-rbac.md` ข้อ 5)
 */

import type {
  CreateSupplierBody,
  Supplier,
  SupplierListPage,
  UpdateSupplierBody,
} from './types';
import { apiFetch } from '../auth/authorizedFetch';
import { buildApiUrl, getConfiguredApiBaseUrl } from '../apiBase';

/**
 * เส้นทางจริงของกลุ่มนี้ — ตาราง endpoint ใน contract ตัด base prefix ออก
 * แต่ URL ที่ client เรียกขึ้นต้นด้วย `/v1` เสมอ
 */
export const SUPPLIERS_PATH = '/v1/suppliers';

/**
 * เส้นทางของผู้ขายรายเดียว — `supplier_id` เป็น **UUID surrogate** (D-039)
 * ไม่ใช่ `supplier_code` ซึ่งเป็น business key
 *
 * `encodeURIComponent` ไว้ด้วยแม้ UUID จะไม่มีอักขระที่ต้อง escape: ค่าที่เดินทาง
 * มาถึงที่นี่มาจาก payload ของเซิร์ฟเวอร์ และค่าที่อ่านมาแล้วแปะลง URL ดิบ ๆ คือ
 * วิธีที่คำขอจะเปลี่ยน**เส้นทาง**ทั้งเส้นในวันที่ payload ไม่ใช่สิ่งที่คิดไว้
 */
export function supplierPath(supplierId: string): string {
  return `${SUPPLIERS_PATH}/${encodeURIComponent(supplierId)}`;
}

/**
 * พารามิเตอร์ของ `GET /v1/suppliers` ที่ contract รับ — และไม่มีตัวที่สี่
 *
 * ไม่มีพารามิเตอร์ค้นหาเลยโดยตั้งใจของ contract ช่องค้นหาบนหน้าจอจึงกรอง
 * **ฝั่งเบราว์เซอร์จากหน้าที่โหลดมาแล้ว** ไม่ใช่ยิงคำขอใหม่ (ดู `suppliersView.ts`)
 */
export interface SupplierListQuery {
  readonly status?: string;
  readonly limit?: number;
  readonly cursor?: string;
}

/**
 * ประกอบ query string — คีย์ที่ไม่ได้ระบุ (หรือว่างเปล่า) ไม่เดินทางไปเลย
 *
 * `cursor` ที่เป็นสตริงว่างถูกทิ้ง ไม่ใช่ส่งไปเป็นค่าว่าง: cursor ว่างไม่ใช่
 * "หน้าแรก" สำหรับเซิร์ฟเวอร์ แต่เป็นค่าที่มันจะปฏิเสธว่าไม่ใช่ cursor ที่ตนออกให้
 */
export function buildSupplierListQuery(query: SupplierListQuery = {}): string {
  const params = new URLSearchParams();
  const status = query.status?.trim() ?? '';
  if (status !== '') {
    params.append('status', status);
  }
  if (query.limit !== undefined) {
    params.append('limit', String(query.limit));
  }
  const cursor = query.cursor?.trim() ?? '';
  if (cursor !== '') {
    params.append('cursor', cursor);
  }
  const serialized = params.toString();
  return serialized === '' ? '' : `?${serialized}`;
}

/** Envelope ของ error ตาม `api/errors.py` (`error_code`, `message`, `details`) */
export interface ApiErrorEnvelope {
  readonly error_code: string | null;
  readonly message: string | null;
  /**
   * `details.rule` — วิธีที่ contract ใช้แยกกรณีทางธุรกิจออกจากกันโดยไม่เพิ่ม
   * top-level error code ใหม่ ในกลุ่มนี้มันเป็นตัวเดียวที่แยก
   * `SUPPLIER_CODE_IS_IMMUTABLE` ออกจาก `SUPPLIER_FIELD_IS_SERVER_OWNED` และจาก
   * `400` ธรรมดาของ payload ที่ผิดรูป — ทั้งสามมาด้วย `400` เท่ากัน
   */
  readonly rule: string | null;
  readonly details: Readonly<Record<string, unknown>>;
}

const EMPTY_ENVELOPE: ApiErrorEnvelope = {
  error_code: null,
  message: null,
  rule: null,
  details: {},
};

/**
 * ความล้มเหลวทุกแบบที่ contract นิยามไว้ แยกเป็นสถานะของตัวเอง
 *
 * `403` ของกลุ่มนี้แปลว่า **แผนกของคุณไม่ได้ถือโมดูล `SUPPLIER_MASTER`** ไม่ใช่
 * "โรงงานนอกขอบเขต": ตาราง supplier เป็นข้อมูลระดับองค์กรและไม่มี `plant_id` เลย
 * (D-083) จึงไม่มี plant scope ให้ชนตั้งแต่ต้น — และเพราะมันเป็นโมดูลธุรกิจปกติ
 * `ADMIN` ก็ถูกปฏิเสธด้วย `403` เช่นกัน (D-060)
 *
 * `404` แปลว่า **ผู้ขายรายที่ `PATCH` อ้างถึงไม่มีอยู่แล้ว** ซึ่งเกิดได้จริงจาก
 * รายการที่เปิดค้างไว้ขณะที่อีกคนแก้ข้อมูล — ไม่ใช่ข้อผิดพลาดของระบบ
 *
 * `conflict` (`409`) ต้องเป็นสถานะของตัวเอง **ไม่ใช่ `server-error`**: รหัสผู้ขาย
 * ที่ซ้ำเป็นสิ่งที่ผู้ใช้แก้เองได้ในสองวินาที ส่วน "เซิร์ฟเวอร์ทำงานไม่สำเร็จ" คือ
 * สิ่งที่เขาทำอะไรไม่ได้เลย
 *
 * `rule-violation` (`422`) อยู่ในชุดนี้เพื่อความครบถ้วนของ envelope ฝั่งเซิร์ฟเวอร์
 * — กลุ่มนี้ประกาศ `422` ไว้ใน OpenAPI ของ route รายรายการ แม้ปัจจุบันจะยังไม่มีกฎ
 * ธุรกิจข้อใดที่ตอบด้วยรหัสนั้น การกลืนมันเป็น `server-error` จะทำให้กฎข้อแรกที่
 * ถูกเพิ่มในอนาคตอ่านเหมือนระบบพัง
 */
export type SupplierFailure =
  | { readonly status: 'unauthenticated' }
  | { readonly status: 'forbidden'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'not-found'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'conflict'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'rule-violation'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'bad-request'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'server-error'; readonly httpStatus: number }
  | { readonly status: 'network-error'; readonly detail: string }
  | { readonly status: 'invalid-response' }
  | { readonly status: 'cancelled' };

export type SupplierListResult =
  | { readonly status: 'ready'; readonly page: SupplierListPage }
  | SupplierFailure;

export type SupplierWriteResult =
  | { readonly status: 'ready'; readonly supplier: Supplier }
  | SupplierFailure;

export interface SupplierRequestOptions {
  /** base path ของ API (default: อ่านจาก `VITE_API_BASE_URL` แบบ same-origin) */
  readonly apiBaseUrl?: string;
  readonly signal?: AbortSignal;
  /**
   * inject ได้เพื่อการทดสอบ
   *
   * ค่าเริ่มต้นคือ `apiFetch` ตัวเดียวกับที่ client อื่นใช้ — header
   * `Authorization: Bearer` ถูกเติมที่นั่นจุดเดียว ไม่ใช่ที่ client แต่ละตัว
   */
  readonly fetchImpl?: typeof fetch;
}

/* -------------------------------------------------------------- parsing -- */

function requiredString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

/**
 * ฟิลด์ที่ nullable ได้ — `undefined` แปลว่า "ผิดรูป"
 *
 * แยก "ไม่มีค่า" (null) ออกจาก "ชนิดผิด" (undefined) โดยตั้งใจ
 */
function nullableString(value: unknown): string | null | undefined {
  if (value === null || value === undefined) {
    return null;
  }
  return typeof value === 'string' ? value : undefined;
}

/**
 * `next_cursor` ของ envelope — `undefined` แปลว่า "ผิดรูป" ไม่ใช่ "ไม่มีหน้าถัดไป"
 *
 * cursor ที่เป็นสตริงว่างไม่ใช่ "มีหน้าถัดไป" — ปุ่มโหลดเพิ่มที่ส่ง cursor ว่าง
 * จะวนขอหน้าเดิมซ้ำไปเรื่อย ๆ
 */
function parseCursor(raw: unknown): string | null | undefined {
  const cursor = nullableString(raw);
  if (cursor === undefined) {
    return undefined;
  }
  return cursor === null || cursor.trim() === '' ? null : cursor;
}

/**
 * หนึ่งผู้ขาย — **บังคับครบทั้งหกฟิลด์ที่ contract บังคับ**
 *
 * `status` ถูกอ่านเป็นสตริงอะไรก็ได้ที่ไม่ว่าง ไม่ใช่ union ของสามค่าที่หน้าจอรู้จัก:
 * CHECK constraint ฝั่งเซิร์ฟเวอร์เป็นผู้ถือชุดที่รับได้ และการกรองค่าที่สี่ทิ้ง
 * ตรงนี้จะทำให้ **ผู้ขายที่มีอยู่จริงหายไปจากรายการ** ในวันที่ชุดนั้นถูกขยาย ซึ่ง
 * แย่กว่าการแสดงรหัสที่หน้าจอยังไม่มีคำอธิบายให้ (ดู `supplierStatusLabel`)
 */
export function parseSupplier(raw: unknown): Supplier | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;

  const supplierId = requiredString(record.supplier_id);
  const supplierCode = requiredString(record.supplier_code);
  const supplierName = requiredString(record.supplier_name);
  const status = requiredString(record.status);
  const createdAt = requiredString(record.created_at);
  const updatedAt = requiredString(record.updated_at);

  if (
    supplierId === null ||
    supplierCode === null ||
    supplierName === null ||
    status === null ||
    createdAt === null ||
    updatedAt === null
  ) {
    return null;
  }

  return {
    supplier_id: supplierId,
    supplier_code: supplierCode,
    supplier_name: supplierName,
    status,
    created_at: createdAt,
    updated_at: updatedAt,
  };
}

/**
 * อ่านรายการผู้ขายจาก envelope `{ items, next_cursor }` — **และจากที่นั่นเท่านั้น**
 *
 * contract freeze รูปนี้ไว้ตรง ๆ จึงไม่รับ array เปล่าเป็นทางถอย — parser ที่เขียน
 * ขึ้นจากรูปที่เดาเอาเองคือ defect ที่ทำให้หน้าจอ BOM เคยขึ้นว่า "ยังไม่มีสูตร"
 * ให้สินค้าที่มีสูตรอยู่แล้ว
 *
 * แถวเดียวที่อ่านไม่ออกทำให้ **ทั้งหน้าผิดรูป** ไม่ใช่ถูกตัดทิ้งเงียบ ๆ: รายการที่
 * ขาดไปหนึ่งแถวคือหน้าจอที่บอกผู้ใช้ว่ารหัสนั้นยังว่างอยู่ แล้วเขาจะกดสร้างซ้ำ
 */
export function parseSupplierListPage(raw: unknown): SupplierListPage | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;
  if (!Array.isArray(record.items)) {
    return null;
  }

  const items: Supplier[] = [];
  for (const entry of record.items) {
    const parsed = parseSupplier(entry);
    if (parsed === null) {
      return null;
    }
    items.push(parsed);
  }

  const cursor = parseCursor(record.next_cursor);
  return cursor === undefined ? null : { items, next_cursor: cursor };
}

function parseErrorEnvelope(raw: unknown): ApiErrorEnvelope {
  if (typeof raw !== 'object' || raw === null) {
    return EMPTY_ENVELOPE;
  }
  const record = raw as Record<string, unknown>;
  const details =
    typeof record.details === 'object' && record.details !== null
      ? (record.details as Record<string, unknown>)
      : {};

  return {
    error_code: requiredString(record.error_code),
    message: requiredString(record.message),
    rule: requiredString(details.rule),
    details,
  };
}

/* ---------------------------------------------------------- serializing -- */

/**
 * แปลง body ของ `POST /v1/suppliers` ให้เหลือเฉพาะคีย์ที่ contract รับ
 *
 * เขียนคีย์ที่ออกไปทีละตัวแทนการ spread state ของฟอร์ม ด้วยเหตุผลเดียวกับ
 * `serializeCreateProductBody()`: `extra = "forbid"` ทำให้คีย์แปลกปลอมที่ติดมา
 * (เช่นแถวของผู้ขายที่ถูก spread ทั้งก้อนพร้อม `supplier_id`) กลายเป็น `400`
 * ทั้งที่ผู้ใช้กรอกถูกทุกช่อง — และเป็นด่านที่ทำให้ `supplier_id` ไปถึงเซิร์ฟเวอร์
 * **ไม่ได้เลย** ต่อให้ผู้เรียกถือ object ที่มีคีย์นั้นอยู่ในมือ
 *
 * `status` หายไปทั้งคีย์เมื่อไม่ได้ระบุ ไม่ใช่ส่ง `null` หรือสตริงว่าง: contract
 * บอกว่ามันไม่บังคับและเซิร์ฟเวอร์เติม `ACTIVE` ให้
 */
export function serializeCreateSupplierBody(
  body: CreateSupplierBody,
): Record<string, unknown> {
  return {
    supplier_code: body.supplier_code,
    supplier_name: body.supplier_name,
    ...(body.status === undefined ? {} : { status: body.status }),
  };
}

/**
 * แปลง body ของ `PATCH /v1/suppliers/{supplier_id}` — **`supplier_code` ออกไปไม่ได้**
 *
 * ด่านสุดท้ายของกฎที่ `UpdateSupplierBody` ประกาศไว้ด้วยชนิดข้อมูล: คีย์ที่ไม่ได้
 * ระบุจะไม่เดินทางไปเลย ("ปล่อยไว้ตามเดิม") และรหัสผู้ขายไม่มีทางหลุดไปกับคำขอ
 * ซึ่งจะถูกตอบ `400 SUPPLIER_CODE_IS_IMMUTABLE` ทั้งที่ผู้ใช้แค่แก้ชื่อ
 */
export function serializeUpdateSupplierBody(
  body: UpdateSupplierBody,
): Record<string, unknown> {
  return {
    ...(body.supplier_name === undefined ? {} : { supplier_name: body.supplier_name }),
    ...(body.status === undefined ? {} : { status: body.status }),
  };
}

/* ------------------------------------------------------------- requests -- */

async function readEnvelope(response: Response): Promise<ApiErrorEnvelope> {
  try {
    return parseErrorEnvelope(await response.json());
  } catch {
    // error body ที่อ่านไม่ออกไม่ควรกลบสถานะ HTTP ที่อ่านออกแล้ว
    return EMPTY_ENVELOPE;
  }
}

type PayloadResult =
  | { readonly status: 'payload'; readonly payload: unknown }
  | SupplierFailure;

async function requestPayload(
  path: string,
  init: RequestInit,
  options: SupplierRequestOptions,
): Promise<PayloadResult> {
  const fetchImpl = options.fetchImpl ?? apiFetch;
  if (typeof fetchImpl !== 'function') {
    return { status: 'network-error', detail: 'fetch ไม่พร้อมใช้งานใน runtime นี้' };
  }

  const baseUrl = options.apiBaseUrl ?? getConfiguredApiBaseUrl();
  const url = buildApiUrl(baseUrl, path);

  let response: Response;
  try {
    response = await fetchImpl(url, {
      credentials: 'same-origin',
      cache: 'no-store',
      ...init,
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch (error) {
    if (options.signal?.aborted || (error as Error | undefined)?.name === 'AbortError') {
      return { status: 'cancelled' };
    }
    return {
      status: 'network-error',
      detail: error instanceof Error ? error.message : 'ไม่ทราบสาเหตุ',
    };
  }

  if (options.signal?.aborted) {
    return { status: 'cancelled' };
  }

  switch (response.status) {
    case 400:
      return { status: 'bad-request', error: await readEnvelope(response) };
    case 401:
      return { status: 'unauthenticated' };
    case 403:
      return { status: 'forbidden', error: await readEnvelope(response) };
    case 404:
      return { status: 'not-found', error: await readEnvelope(response) };
    case 409:
      return { status: 'conflict', error: await readEnvelope(response) };
    case 422:
      return { status: 'rule-violation', error: await readEnvelope(response) };
    default:
      break;
  }

  if (!response.ok) {
    return { status: 'server-error', httpStatus: response.status };
  }

  try {
    return { status: 'payload', payload: await response.json() };
  } catch {
    return { status: 'invalid-response' };
  }
}

/**
 * `GET /v1/suppliers` — vendor master ทั้งองค์กร เรียงตาม `supplier_code`
 *
 * **ไม่เรียงลำดับซ้ำเอง** contract ระบุว่าเซิร์ฟเวอร์เป็นผู้เรียง การเรียงซ้ำที่
 * frontend จะกลบกรณีที่เซิร์ฟเวอร์เรียงผิดจนไม่มีใครเห็น
 */
export async function fetchSuppliers(
  query: SupplierListQuery = {},
  options: SupplierRequestOptions = {},
): Promise<SupplierListResult> {
  const result = await requestPayload(
    `${SUPPLIERS_PATH}${buildSupplierListQuery(query)}`,
    { method: 'GET', headers: { Accept: 'application/json' } },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }

  const page = parseSupplierListPage(result.payload);
  return page === null ? { status: 'invalid-response' } : { status: 'ready', page };
}

/**
 * `POST /v1/suppliers` — สร้างผู้ขายใหม่ ตอบ `201`
 *
 * **ไม่มี `Idempotency-Key`**: contract สงวน header นั้นไว้ให้ Scan, Goods Receipt,
 * Initial Import และ Offline Sync ที่กระทบ stock/บัญชี การสร้าง vendor master ไม่
 * เคลื่อนสต๊อกหรือบัญชี และรหัสที่ซ้ำถูกกันด้วย unique constraint อยู่แล้ว
 * (`409 SUPPLIER_CODE_TAKEN` โดยไม่เขียนอะไรเลย) การเติมคีย์ที่ endpoint ไม่ได้ขอ
 * เข้าไปคือ header ที่ไม่มีใครอ่าน ซึ่งจะสอนคนอ่านโค้ดผิดว่า endpoint นี้ต้องการมัน
 */
export async function createSupplier(
  body: CreateSupplierBody,
  options: SupplierRequestOptions = {},
): Promise<SupplierWriteResult> {
  const result = await requestPayload(
    SUPPLIERS_PATH,
    {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify(serializeCreateSupplierBody(body)),
    },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }

  const supplier = parseSupplier(result.payload);
  return supplier === null ? { status: 'invalid-response' } : { status: 'ready', supplier };
}

/**
 * `PATCH /v1/suppliers/{supplier_id}` — แก้ชื่อ และ/หรือ สถานะ
 *
 * เซิร์ฟเวอร์เขียน `audit_log` action `UPDATE` (before/after) **เสมอแม้ไม่มีอะไร
 * เปลี่ยน** ในธุรกรรมเดียวกับการแก้ข้อมูล — ผู้เรียกจึงอ่านสถานะใหม่จาก **คำตอบ**
 * ไม่ใช่จากสิ่งที่ตัวเองเพิ่งส่งไป
 */
export async function updateSupplier(
  supplierId: string,
  body: UpdateSupplierBody,
  options: SupplierRequestOptions = {},
): Promise<SupplierWriteResult> {
  const result = await requestPayload(
    supplierPath(supplierId),
    {
      method: 'PATCH',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify(serializeUpdateSupplierBody(body)),
    },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }

  const supplier = parseSupplier(result.payload);
  return supplier === null ? { status: 'invalid-response' } : { status: 'ready', supplier };
}

/**
 * อ่าน `details.rule` จากความล้มเหลวที่มี envelope — คืน `null` เมื่อไม่มี
 *
 * ไม่เดาค่าเมื่ออ่านไม่ได้ เพราะกฎที่เดาผิดจะส่งผู้ใช้ไปแก้เรื่องที่ไม่ได้เสีย
 */
export function ruleFromFailure(failure: SupplierFailure): string | null {
  return 'error' in failure ? failure.error.rule : null;
}
